/**
 * What was last copied in the chord bars builder, so it can be pasted elsewhere: a whole chart, into
 * another part, or one bar, onto another bar.
 *
 * Each is held as text. Kept in sessionStorage so it outlives the modal and a refresh, with a copy in
 * memory for when storage is blocked.
 */
const createSlot = (storageKey) => {
  let memory = "";
  const listeners = new Set();

  const read = () => {
    try {
      return window.sessionStorage.getItem(storageKey) ?? memory;
    } catch {
      return memory;
    }
  };

  const copy = (text) => {
    memory = text;

    try {
      window.sessionStorage.setItem(storageKey, text);
    } catch {
      // Memory still has it
    }

    listeners.forEach((listener) => listener(text));
  };

  const subscribe = (listener) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
  };

  return { read, copy, subscribe };
};

// The chart text the part itself stores
const chartSlot = createSlot("chordBarsClipboard");

export const readChart = chartSlot.read;
export const copyChart = chartSlot.copy;
export const subscribeChart = chartSlot.subscribe;

// One grid cell, with the beats per bar it was written in — a bar's text is only read the same way
// under the same meter
const barSlot = createSlot("chordBarsBarClipboard");

const parseBar = (text) => {
  try {
    const bar = JSON.parse(text);

    return typeof bar?.cell === "string" && typeof bar?.beatsPerBar === "number" ? bar : null;
  } catch {
    return null;
  }
};

export const readBar = () => parseBar(barSlot.read());
export const copyBar = (cell, beatsPerBar) => barSlot.copy(JSON.stringify({ cell, beatsPerBar }));
export const subscribeBar = (listener) => barSlot.subscribe((text) => listener(parseBar(text)));
